// pkg169 — async extraction: 'processing' rows show a progress panel and poll every 5s until the questions arrive; 'failed' rows show processing_error with Delete / Delete-and-upload-again — delivered 2026-09-10
// pkg72b — teal CTA button(s) #1a7a5e → NAVY #1E2761; semantic status dots/greens kept — delivered 2026-07-12
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';


// The Discovery Response workspace (attorney side). Three main interactions:
//   1) Edit the response's caption / metadata (collapsible panel up top).
//   2) Review/edit the extracted question list — left rail (read-only,
//      click-to-switch only; the opposing counsel's question set isn't
//      something we add to, delete from, or reorder), and the per-question
//      editor on the right: question type/number/text, answer/notes/approved.
//   3) Generate the final .docx via a Generate panel (attorney picker + COS).
//
// Privacy mode notes for this page:
//   - The client's name in the header is masked.
//   - The download filename uses the masked name so the file the recorder
//     downloads doesn't leak the real client name in the filename.
//   - Question text, answers, and attorney notes are NOT redacted, because
//     these are the legitimate subject of any tutorial about the discovery
//     workflow. If you're recording, prepare a sanitized test case rather
//     than real questions.
//   - Caption fields (plaintiff/defendant/opposing_counsel) are EDIT inputs
//     so they show real values — the banner reminds you not to save.
//   - The presence indicator's "user_name" (which would identify the client
//     when they're viewing) is masked.

const TYPE_LABELS = {
  interrogatory: 'Interrogatory',
  rfp: 'Request for Production',
  rfa: 'Request for Admission'
};

const TYPE_BADGE_COLORS = {
  interrogatory: { bg: '#e8f0fb', fg: '#2f5496', border: '#bcd4ee' },
  rfp: { bg: '#f5f0fa', fg: '#5b3a8c', border: '#dcc8ee' },
  rfa: { bg: '#fdf2e7', fg: '#d68910', border: '#f5c98e' }
};

const PRESENCE_HEARTBEAT_MS = 10000;
const PRESENCE_FRESH_MS = 30000;

// Visibility toggle button definitions.
const STATUS_OPTIONS = [
{ value: 'draft', label: 'Draft', desc: 'Only your firm can see this. The client cannot view it.' },
{ value: 'visible_to_client', label: 'Visible to client', desc: 'Client can view AND edit their answers. Edits are visible to your firm in real time.' },
{ value: 'locked', label: 'Locked', desc: 'Frozen — neither side can edit. Use this once the document has been generated and served.' }];


function TypeBadge({ type }) {
  const c = TYPE_BADGE_COLORS[type] || TYPE_BADGE_COLORS.interrogatory;
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3,
      background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
      fontFamily: 'Arial, sans-serif', display: 'inline-block', textTransform: 'uppercase',
      letterSpacing: '0.04em'
    }}>{TYPE_LABELS[type] || type}</span>);

}

function formatDateTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function AdminDiscoveryResponseWorkspacePage() {
  const navigate = useNavigate();
  const { id } = useParams();


  const [response, setResponse] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeQid, setActiveQid] = useState(null);

  // Caption-panel state
  const [captionOpen, setCaptionOpen] = useState(false);
  const [captionDraft, setCaptionDraft] = useState({});
  const [savingCaption, setSavingCaption] = useState(false);

  // Per-question editing state (kept locally so we can save explicitly)
  const [qDraft, setQDraft] = useState({
    question_type: 'interrogatory', question_number: '', question_text: '',
    answer_text: '', approved: false, attorney_notes: '', version: 0
  });
  const [savingQuestion, setSavingQuestion] = useState(false);
  const [savingAnswer, setSavingAnswer] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Visibility toggle
  const [savingStatus, setSavingStatus] = useState(false);

  // Presence (other party viewing). Bucketed by question_id; we strip out
  // our own (admin) rows so we only render client viewers.
  const [presenceByQ, setPresenceByQ] = useState({});

  // Conflict modal state.
  const [conflict, setConflict] = useState(null);
  // conflict shape: { localDraft: { answer_text, approved, attorney_notes }, serverRow, mergeText, mode: 'pick'|'merge' }

  // Generate panel
  const [genOpen, setGenOpen] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [genState, setGenState] = useState({
    attorneyIds: [], signingAttorneyId: '', allowUnapproved: false,
    includePreamble: true
  });
  const [generating, setGenerating] = useState(false);

  // Firm-wide discovery defaults (currently just the preamble). Loaded once
  // on mount; drives the "Include the default preamble" option in the
  // Generate modal. Editing the default lives in Admin → Discovery Settings.
  const [discoveryDefaults, setDiscoveryDefaults] = useState({ discovery_response_preamble: '' });

  // ─── Load ────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);setError('');
    try {
      const data = await adminApi.getDiscoveryResponse(id);
      setResponse(data.response);
      setQuestions(data.questions);
      setCaptionDraft(data.response);
      if (data.questions.length && !activeQid) {
        setActiveQid(data.questions[0].id);
      } else if (data.questions.length && activeQid) {
        // Make sure the currently active question still exists.
        if (!data.questions.find((q) => q.id === activeQid)) {
          setActiveQid(data.questions[0].id);
        }
      } else if (!data.questions.length) {
        setActiveQid(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
    adminApi.getAdminUsers().then(setAdmins).catch(() => {});
    adminApi.getDiscoverySettings().then(setDiscoveryDefaults).catch(() => {});
  }, [id]);

  // pkg169 — while the background extraction runs, poll every 5s; the row
  // flips to 'draft' (questions arrive) or 'failed' (error shown below).
  useEffect(() => {
    if (!response || response.status !== 'processing') return;
    let cancelled = false;
    const t = setInterval(async () => {
      try {
        const data = await adminApi.getDiscoveryResponse(id);
        if (cancelled) return;
        if (data.response.status !== 'processing') {
          setResponse(data.response);
          setQuestions(data.questions);
          setCaptionDraft(data.response);
          if (data.questions.length) setActiveQid(data.questions[0].id);
        } else if (data.response.processing_progress !== response.processing_progress) {
          setResponse((prev) => ({ ...prev, processing_progress: data.response.processing_progress }));
        }
      } catch (_) { /* keep polling */ }
    }, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [id, response && response.status, response && response.processing_progress]);

  // When activeQid changes, sync qDraft from the loaded question
  useEffect(() => {
    if (!activeQid) return;
    const q = questions.find((x) => x.id === activeQid);
    if (!q) return;
    setQDraft({
      question_type: q.question_type,
      question_number: q.question_number || '',
      question_text: q.question_text || '',
      answer_text: q.answer_text || '',
      approved: !!q.approved,
      attorney_notes: q.attorney_notes || '',
      version: q.version
    });
    setHasUnsavedChanges(false);
  }, [activeQid, questions]);

  // Warn on tab close if there are unsaved changes
  useEffect(() => {
    function before(e) {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    }
    window.addEventListener('beforeunload', before);
    return () => window.removeEventListener('beforeunload', before);
  }, [hasUnsavedChanges]);

  // ─── Presence (Phase 2) ──────────────────────────────────────────────────

  const heartbeatTimer = useRef(null);
  const pollTimer = useRef(null);

  useEffect(() => {
    if (!activeQid || !response) return;
    // Presence is only meaningful when the response is visible_to_client.
    // For draft, the client isn't there. For locked, nobody is editing.
    if (response.status !== 'visible_to_client') return;

    let cancelled = false;

    async function beat() {
      try {await adminApi.sendDiscoveryResponsePresence(id, activeQid);} catch (_) {/* silent */}
    }
    async function poll() {
      try {
        const rows = await adminApi.getDiscoveryResponsePresence(id);
        if (cancelled) return;
        const cutoff = Date.now() - PRESENCE_FRESH_MS;
        const byQ = {};
        for (const r of rows || []) {
          if (r.user_type === 'admin') continue; // hide our side
          const t = new Date(r.last_seen_at).getTime();
          if (isNaN(t) || t < cutoff) continue;
          (byQ[r.question_id] = byQ[r.question_id] || []).push(r);
        }
        setPresenceByQ(byQ);
      } catch (_) {/* silent */}
    }

    beat();poll();
    heartbeatTimer.current = setInterval(beat, PRESENCE_HEARTBEAT_MS);
    pollTimer.current = setInterval(poll, PRESENCE_HEARTBEAT_MS);

    return () => {
      cancelled = true;
      if (heartbeatTimer.current) clearInterval(heartbeatTimer.current);
      if (pollTimer.current) clearInterval(pollTimer.current);
      heartbeatTimer.current = null;
      pollTimer.current = null;
    };
  }, [id, activeQid, response]);

  function setQ(k, v) {
    setQDraft((p) => ({ ...p, [k]: v }));
    setHasUnsavedChanges(true);
  }

  const activeQuestion = activeQid ? questions.find((x) => x.id === activeQid) : null;

  // ─── Caption save ─────────────────────────────────────────────────────────

  async function saveCaption(e) {
    e.preventDefault();
    setSavingCaption(true);setError('');
    try {
      const updated = await adminApi.updateDiscoveryResponse(id, {
        title: captionDraft.title,
        caption_court: captionDraft.caption_court,
        plaintiff: captionDraft.plaintiff,
        defendant: captionDraft.defendant,
        docket_no: captionDraft.docket_no,
        division: captionDraft.division,
        ryan_represents: captionDraft.ryan_represents,
        opposing_counsel: captionDraft.opposing_counsel,
        response_doc_title: captionDraft.response_doc_title
      });
      setResponse((prev) => ({ ...prev, ...updated }));
      setCaptionOpen(false);
    } catch (err) {setError(err.message);} finally
    {setSavingCaption(false);}
  }

  // ─── Visibility toggle (Phase 2) ──────────────────────────────────────────

  async function changeStatus(newStatus) {
    if (!response || response.status === newStatus) return;
    // Warn the attorney before flipping back to draft — that removes
    // visibility from the client. (Not a hard block: there are legitimate
    // reasons to un-share, e.g. uploaded the wrong file.)
    if (newStatus === 'draft' && response.status === 'visible_to_client') {
      if (!window.confirm('Switch this response back to Draft? The client will no longer be able to see or edit it. Any unsaved client edits will be lost.')) {
        return;
      }
    }
    if (newStatus === 'locked' && response.status === 'visible_to_client') {
      if (!window.confirm('Lock this response? Neither you nor the client will be able to edit answers anymore. Use this once the Word document has been generated and served.')) {
        return;
      }
    }
    setSavingStatus(true);setError('');
    try {
      const updated = await adminApi.setDiscoveryResponseStatus(id, newStatus);
      setResponse((prev) => ({ ...prev, ...updated }));
    } catch (err) {
      setError(err.message || 'Could not change visibility.');
    } finally {
      setSavingStatus(false);
    }
  }

  // ─── Question save (text/type/number) ─────────────────────────────────────

  async function saveQuestionText() {
    if (!activeQuestion) return;
    setSavingQuestion(true);setError('');
    try {
      await adminApi.updateDiscoveryQuestion(id, activeQuestion.id, {
        question_type: qDraft.question_type,
        question_number: qDraft.question_number,
        question_text: qDraft.question_text
      });
      await load();
    } catch (err) {setError(err.message);} finally
    {setSavingQuestion(false);}
  }

  // ─── Answer save (with version + conflict modal) ──────────────────────────

  async function saveAnswer() {
    if (!activeQuestion) return;
    setSavingAnswer(true);setError('');
    try {
      const updated = await adminApi.saveDiscoveryAnswer(id, activeQuestion.id, {
        answer_text: qDraft.answer_text,
        approved: qDraft.approved,
        attorney_notes: qDraft.attorney_notes,
        version: qDraft.version
      });
      setQuestions((prev) => prev.map((q) => q.id === updated.id ? updated : q));
      setQDraft((p) => ({ ...p, version: updated.version }));
      setHasUnsavedChanges(false);
    } catch (err) {
      if (err.status === 409 && err.current) {
        // Phase 2: client beat us to it. Open the conflict modal with both
        // versions and let the attorney decide. attorney_notes and
        // approved are admin-only fields so they don't conflict — only
        // answer_text can have been touched on the other side.
        setConflict({
          localDraft: {
            answer_text: qDraft.answer_text,
            approved: qDraft.approved,
            attorney_notes: qDraft.attorney_notes
          },
          serverRow: err.current,
          mergeText: err.current.answer_text || '',
          mode: 'pick'
        });
      } else {
        setError(err.message);
      }
    } finally {
      setSavingAnswer(false);
    }
  }

  // ─── Conflict modal resolution ────────────────────────────────────────────

  async function resolveKeepMine() {
    if (!conflict) return;
    setSavingAnswer(true);setError('');
    try {
      const updated = await adminApi.saveDiscoveryAnswer(id, activeQid, {
        answer_text: conflict.localDraft.answer_text,
        approved: conflict.localDraft.approved,
        attorney_notes: conflict.localDraft.attorney_notes,
        version: conflict.serverRow.version
      });
      setQuestions((prev) => prev.map((q) => q.id === updated.id ? updated : q));
      setQDraft({
        question_type: updated.question_type,
        question_number: updated.question_number || '',
        question_text: updated.question_text || '',
        answer_text: updated.answer_text || '',
        approved: !!updated.approved,
        attorney_notes: updated.attorney_notes || '',
        version: updated.version
      });
      setHasUnsavedChanges(false);
      setConflict(null);
    } catch (err) {setError(err.message);} finally
    {setSavingAnswer(false);}
  }

  function resolveKeepTheirs() {
    if (!conflict) return;
    const srv = conflict.serverRow;
    setQuestions((prev) => prev.map((q) => q.id === srv.id ? { ...q, ...srv } : q));
    // Keep attorney's local approved/notes; only answer_text comes from server.
    setQDraft((p) => ({
      ...p,
      answer_text: srv.answer_text || '',
      version: srv.version
    }));
    setHasUnsavedChanges(false);
    setConflict(null);
  }

  async function resolveMerge() {
    if (!conflict) return;
    setSavingAnswer(true);setError('');
    try {
      const updated = await adminApi.saveDiscoveryAnswer(id, activeQid, {
        answer_text: conflict.mergeText,
        approved: conflict.localDraft.approved,
        attorney_notes: conflict.localDraft.attorney_notes,
        version: conflict.serverRow.version
      });
      setQuestions((prev) => prev.map((q) => q.id === updated.id ? updated : q));
      setQDraft((p) => ({
        ...p,
        answer_text: updated.answer_text || '',
        version: updated.version
      }));
      setHasUnsavedChanges(false);
      setConflict(null);
    } catch (err) {
      if (err.status === 409 && err.current) {
        // Someone changed it again while merging. Surface the new server row.
        setConflict((prev) => prev ? { ...prev, serverRow: err.current } : null);
        setError('The client edited again while you were merging. Latest version loaded \u2014 review and try again.');
      } else {
        setError(err.message);
      }
    } finally {setSavingAnswer(false);}
  }

  // ─── Delete entire response ───────────────────────────────────────────────

  async function deleteResponse() {
    if (!window.confirm('Delete this entire discovery response, including all questions and answers? This cannot be undone.')) return;
    try {
      await adminApi.deleteDiscoveryResponse(id);
      navigate('/admin/dashboard/discovery-responses');
    } catch (err) {setError(err.message);}
  }

  // ─── Generate ─────────────────────────────────────────────────────────────

  function toggleAttorney(aid) {
    setGenState((p) => {
      const set = new Set(p.attorneyIds);
      if (set.has(aid)) set.delete(aid);else set.add(aid);
      const arr = Array.from(set);
      const signing = arr.includes(p.signingAttorneyId) ? p.signingAttorneyId : arr[0] || '';
      return { ...p, attorneyIds: arr, signingAttorneyId: signing };
    });
  }

  async function handleGenerate() {
    setGenerating(true);setError('');
    try {
      const blob = await adminApi.generateDiscoveryResponse(id, {
        attorneyIds: genState.attorneyIds,
        signingAttorneyId: genState.signingAttorneyId,
        allowUnapproved: genState.allowUnapproved,
        includePreamble: genState.includePreamble
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Use the masked name in the filename so downloads during recording
      // don't leak via the download filename / "Recent Downloads" toast.
      const displayName = response.client_name || 'Response';
      const safeName = displayName.replace(/[^A-Za-z0-9]+/g, '_');
      a.download = `Discovery_Responses_${safeName}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setGenOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  }

  const eligibleAttorneys = admins.filter((a) =>
  (a.role === 'owner' || a.role === 'attorney') && a.bar_number
  );

  // ─── Stats for header ─────────────────────────────────────────────────────

  const stats = questions.reduce((acc, q) => {
    acc.total++;
    if (q.approved) acc.approved++;
    if (q.question_type === 'interrogatory') acc.iqs++;else
    if (q.question_type === 'rfp') acc.rfps++;else
    if (q.question_type === 'rfa') acc.rfas++;
    return acc;
  }, { total: 0, approved: 0, iqs: 0, rfps: 0, rfas: 0 });

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return <div style={{ padding: 40, fontFamily: 'Arial, sans-serif', color: '#888' }}>Loading...</div>;
  }
  if (!response) {
    return (
      <div style={{ padding: 24 }}>
        <button className="btn btn-sm" onClick={() => navigate('/admin/dashboard/discovery-responses')}>← Back</button>
        <div className="alert alert-error" style={{ marginTop: 16 }}>Response not found.</div>
      </div>);

  }

  // pkg169 — extraction still running / failed: a single panel instead of
  // the workspace (no questions exist yet, so nothing else is actionable).
  if (response.status === 'processing' || response.status === 'failed') {
    const failed = response.status === 'failed';
    return (
      <div>
        <button className="btn btn-sm" onClick={() => navigate('/admin/dashboard/discovery-responses')} style={{ marginBottom: 12 }}>
          ← Back to all responses
        </button>
        <h1 className="page-title" style={{ marginBottom: 4 }}>{response.title || 'Discovery Response'}</h1>
        <p className="page-subtitle">
          Client: <strong>{response.client_name}</strong>
          {response.source_file_name && <span style={{ color: '#888' }}> · {response.source_file_name}</span>}
        </p>
        <div className="card" style={{ marginTop: 16, padding: 20, maxWidth: 640 }}>
          {failed ? (
            <>
              <div style={{ fontFamily: 'Arial, sans-serif', fontWeight: 600, color: '#B85042', marginBottom: 8 }}>Extraction failed</div>
              <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#444', marginBottom: 16 }}>{response.processing_error || 'The document could not be read.'}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" onClick={async () => {
                  try { await adminApi.deleteDiscoveryResponse(id); navigate('/admin/dashboard/discovery-responses/new'); }
                  catch (err) { setError(err.message); }
                }}>Delete and upload again</button>
                <button className="btn" onClick={deleteResponse}>Delete</button>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontFamily: 'Arial, sans-serif', fontWeight: 600, marginBottom: 8 }}>Extracting questions…</div>
              <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#444' }}>{response.processing_progress || 'Reading the document…'}</div>
              <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888', marginTop: 12 }}>
                Scanned PDFs are transcribed page by page and can take a few minutes. You can leave this page — it will be in the list as "Processing" until it's ready.
              </div>
            </>
          )}
          {error && <div className="alert alert-error" style={{ marginTop: 12 }}>{error}</div>}
        </div>
      </div>
    );
  }

  const currentStatus = STATUS_OPTIONS.find((s) => s.value === response.status) || STATUS_OPTIONS[0];
  const editedByClient = activeQuestion?.answer_updated_by_type === 'client';
  const clientViewersHere = (presenceByQ[activeQid] || []).filter((p) => p.user_type === 'client');

  return (
    <div>
      <button className="btn btn-sm" onClick={() => navigate('/admin/dashboard/discovery-responses')} style={{ marginBottom: 12 }}>
        ← Back to all responses
      </button>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 12 }}>
        <div style={{ minWidth: 0, flex: '1 1 400px' }}>
          <h1 className="page-title" style={{ marginBottom: 4 }}>
            {response.title || 'Discovery Response'}
          </h1>
          <p className="page-subtitle" style={{ marginBottom: 0 }}>
            Client: <strong>{response.client_name}</strong>
            {response.source_file_name &&
            <span style={{ color: '#888' }}> · {response.source_file_name}</span>
            }
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn" onClick={() => setCaptionOpen((o) => !o)}>
            {captionOpen ? 'Hide Case Details' : 'Edit Case Details'}
          </button>
          <button className="btn btn-primary" onClick={() => setGenOpen(true)} disabled={questions.length === 0}>
            Generate Word Doc
          </button>
        </div>
      </div>

      {/* Phase 2: Visibility toggle */}
      <div className="card" style={{ marginBottom: 12, padding: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{
              fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#888',
              textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: 4
            }}>
              Client Visibility
            </div>
            <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', maxWidth: 520 }}>
              {currentStatus.desc}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 0, border: '1px solid #ddd', borderRadius: 4, overflow: 'hidden' }}>
            {STATUS_OPTIONS.map((opt, i) => {
              const active = response.status === opt.value;
              return (
                <button key={opt.value}
                onClick={() => changeStatus(opt.value)}
                disabled={savingStatus || active}
                style={{
                  border: 'none',
                  borderLeft: i === 0 ? 'none' : '1px solid #ddd',
                  padding: '8px 14px',
                  background: active ? '#0F1838' : '#fff',
                  color: active ? '#fff' : '#0F1838',
                  fontFamily: 'Arial, sans-serif', fontSize: 12, fontWeight: 600,
                  cursor: active ? 'default' : 'pointer',
                  opacity: savingStatus && !active ? 0.6 : 1
                }}>
                  {opt.label}
                </button>);

            })}
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
        <StatCard label="Questions" value={stats.total} />
        <StatCard label="Approved" value={`${stats.approved} / ${stats.total}`}
        color={stats.total > 0 && stats.approved === stats.total ? '#1a7a5e' : '#0F1838'} />
        <StatCard label="Interrogatories" value={stats.iqs} />
        <StatCard label="RFPs" value={stats.rfps} />
        <StatCard label="RFAs" value={stats.rfas} />
      </div>

      {/* Caption editor — collapsible */}
      {/* Note: these inputs are an EDIT form so they intentionally show real
           values. Editing while privacy mode is on will save real values
           (which is what you want for actual case work). The banner reminds
           you not to save dummy values during recording. */}
      {captionOpen &&
      <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-title" style={{ fontSize: 14 }}>Case Caption &amp; Metadata</div>
          <form onSubmit={saveCaption}>
            <div className="form-row">
              <div className="form-group">
                <label>Title (source document name)</label>
                <input type="text" value={captionDraft.title || ''}
              onChange={(e) => setCaptionDraft((p) => ({ ...p, title: e.target.value }))}
              placeholder="e.g. Plaintiff's First Set of Interrogatories" />
              </div>
              <div className="form-group">
                <label>Response Doc Title (override auto-generated)</label>
                <input type="text" value={captionDraft.response_doc_title || ''}
              onChange={(e) => setCaptionDraft((p) => ({ ...p, response_doc_title: e.target.value }))}
              placeholder="Leave blank to auto-build from caption" />
              </div>
            </div>
            <div className="form-group">
              <label>Court</label>
              <input type="text" value={captionDraft.caption_court || ''}
            onChange={(e) => setCaptionDraft((p) => ({ ...p, caption_court: e.target.value }))} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Plaintiff</label>
                <input type="text" value={captionDraft.plaintiff || ''}
              onChange={(e) => setCaptionDraft((p) => ({ ...p, plaintiff: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Defendant</label>
                <input type="text" value={captionDraft.defendant || ''}
              onChange={(e) => setCaptionDraft((p) => ({ ...p, defendant: e.target.value }))} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Docket No.</label>
                <input type="text" value={captionDraft.docket_no || ''}
              onChange={(e) => setCaptionDraft((p) => ({ ...p, docket_no: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Division</label>
                <input type="text" value={captionDraft.division || ''}
              onChange={(e) => setCaptionDraft((p) => ({ ...p, division: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>We Represent</label>
                <select value={captionDraft.ryan_represents || 'defendant'}
              onChange={(e) => setCaptionDraft((p) => ({ ...p, ryan_represents: e.target.value }))}>
                  <option value="defendant">Defendant</option>
                  <option value="plaintiff">Plaintiff</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Opposing Counsel (for Certificate of Service)</label>
              <textarea value={captionDraft.opposing_counsel || ''}
            onChange={(e) => setCaptionDraft((p) => ({ ...p, opposing_counsel: e.target.value }))}
            rows={3} style={{ width: '100%', fontFamily: 'Arial, sans-serif', fontSize: 13, padding: 8, lineHeight: 1.5 }} />
            </div>
            <div className="btn-row" style={{ marginTop: 8 }}>
              <button type="submit" className="btn btn-primary" disabled={savingCaption}>
                {savingCaption ? <><span className="spinner" />Saving...</> : 'Save Case Details'}
              </button>
              <button type="button" className="btn" onClick={() => {setCaptionDraft(response);setCaptionOpen(false);}} disabled={savingCaption}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      }

      {/* Two-pane: question list + active question editor */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 280px) 1fr', gap: 16 }}>
        {/* Left rail: question list — READ-ONLY (click to switch, no controls).
             Per Ryan: opposing counsel's question set isn't something we add to,
             reorder, or delete from. So the +Add / ↑ / ↓ / ✕ buttons are gone. */}
        <div className="card" style={{ padding: 0, alignSelf: 'flex-start', maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>
          <div style={{
            padding: '10px 12px', borderBottom: '1px solid #eee', background: '#fafafa',
            fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#888',
            textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600
          }}>
            Questions ({questions.length})
          </div>
          {questions.length === 0 ?
          <div style={{ padding: 16, fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888' }}>
              No questions extracted from this document.
            </div> :

          <div>
              {questions.map((q, idx) => {
              const isActive = q.id === activeQid;
              const clientHere = (presenceByQ[q.id] || []).some((p) => p.user_type === 'client');
              const clientEdited = q.answer_updated_by_type === 'client';
              return (
                <div
                  key={q.id}
                  onClick={() => setActiveQid(q.id)}
                  style={{
                    padding: '10px 12px', borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer',
                    background: isActive ? '#fffbe6' : '#fff',
                    borderLeft: isActive ? '3px solid #b7870a' : '3px solid transparent'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{
                      fontSize: 10, fontWeight: 600, padding: '1px 5px', borderRadius: 2,
                      background: '#0F1838', color: '#fff', fontFamily: 'Arial, sans-serif'
                    }}>{idx + 1}</span>
                      <TypeBadge type={q.question_type} />
                      {q.question_number &&
                    <span style={{ fontSize: 10, color: '#888', fontFamily: 'Arial, sans-serif' }}>
                          #{q.question_number}
                        </span>
                    }
                      {q.approved &&
                    <span title="Approved" style={{
                      fontSize: 10, fontWeight: 600, padding: '1px 5px', borderRadius: 2,
                      background: '#1E2761', color: '#fff', fontFamily: 'Arial, sans-serif'
                    }}>✓</span>
                    }
                      {clientHere &&
                    <span title="The client is viewing this question right now" style={{
                      fontSize: 9, fontWeight: 600, padding: '1px 5px', borderRadius: 2,
                      background: '#2f5496', color: '#fff', fontFamily: 'Arial, sans-serif',
                      marginLeft: 'auto'
                    }}>● CLIENT</span>
                    }
                    </div>
                    <div style={{
                    fontFamily: 'Times New Roman, serif', fontSize: 12, color: '#333',
                    lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical', overflow: 'hidden'
                  }}>
                      {q.question_text}
                    </div>
                    {clientEdited &&
                  <div style={{ fontSize: 10, color: '#2f5496', fontFamily: 'Arial, sans-serif', marginTop: 4, fontWeight: 600 }}>
                        Edited by client
                      </div>
                  }
                  </div>);

            })}
            </div>
          }
        </div>

        {/* Right pane: active question */}
        <div>
          {!activeQuestion ?
          <div className="card" style={{ textAlign: 'center', padding: 40, color: '#888', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
              {questions.length === 0 ?
            'No questions in this response yet. Re-upload the source document to extract questions.' :
            'Select a question on the left to edit it.'}
            </div> :

          <>
              {/* Active question banners: presence + edited-by-client marker */}
              {(clientViewersHere.length > 0 || editedByClient) &&
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                  {clientViewersHere.length > 0 &&
              <div style={{
                padding: '6px 10px', borderRadius: 4,
                background: '#e8f0fb', color: '#2f5496', border: '1px solid #bcd4ee',
                fontFamily: 'Arial, sans-serif', fontSize: 12, fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 6
              }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#2f5496', display: 'inline-block' }} />
                      The client is viewing this question right now
                      {clientViewersHere[0].user_name && <span style={{ color: '#2f5496', fontWeight: 400 }}> ({clientViewersHere[0].user_name})</span>}
                    </div>
              }
                  {editedByClient &&
              <div style={{
                padding: '6px 10px', borderRadius: 4,
                background: '#fff5db', color: '#b7870a', border: '1px solid #f0d68a',
                fontFamily: 'Arial, sans-serif', fontSize: 12
              }}>
                      <strong>Edited by client</strong>
                      {activeQuestion.answer_updated_at && <span> · {formatDateTime(activeQuestion.answer_updated_at)}</span>}
                    </div>
              }
                </div>
            }

              {/* Question editor card */}
              <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div className="card-title" style={{ fontSize: 14, margin: 0 }}>The Question</div>
                  <div style={{ fontSize: 11, color: '#888', fontFamily: 'Arial, sans-serif' }}>
                    Question {questions.findIndex((q) => q.id === activeQid) + 1} of {questions.length}
                    {qDraft.version > 0 && <span> · v{qDraft.version}</span>}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group" style={{ flex: '0 0 200px' }}>
                    <label>Type</label>
                    <select value={qDraft.question_type} onChange={(e) => setQ('question_type', e.target.value)}>
                      <option value="interrogatory">Interrogatory</option>
                      <option value="rfp">Request for Production</option>
                      <option value="rfa">Request for Admission</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: '0 0 140px' }}>
                    <label>Original Number</label>
                    <input type="text" value={qDraft.question_number} onChange={(e) => setQ('question_number', e.target.value)}
                  placeholder="e.g. 1, 1.a" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Question text (as it appears in opposing counsel's document)</label>
                  <textarea value={qDraft.question_text} onChange={(e) => setQ('question_text', e.target.value)}
                rows={6}
                style={{ width: '100%', fontFamily: 'Times New Roman, serif', fontSize: 14, padding: 10, lineHeight: 1.5 }} />
                </div>
                <button className="btn btn-sm btn-primary" onClick={saveQuestionText} disabled={savingQuestion}>
                  {savingQuestion ? <><span className="spinner" />Saving...</> : 'Save Question Text'}
                </button>
              </div>

              {/* Answer card */}
              <div className="card">
                <div className="card-title" style={{ fontSize: 14 }}>Your Answer</div>
                <div className="form-group">
                  <label>Answer / Response</label>
                  <textarea value={qDraft.answer_text} onChange={(e) => setQ('answer_text', e.target.value)}
                rows={8}
                placeholder="Type the response here..."
                style={{ width: '100%', fontFamily: 'Times New Roman, serif', fontSize: 14, padding: 10, lineHeight: 1.5 }} />
                </div>
                <div className="form-group">
                  <label>Attorney Notes (internal only — never in generated doc)</label>
                  <textarea value={qDraft.attorney_notes} onChange={(e) => setQ('attorney_notes', e.target.value)}
                rows={3}
                placeholder='e.g. "Ask client to confirm dates" or "Consider supplementing after deposition"'
                style={{ width: '100%', fontFamily: 'Arial, sans-serif', fontSize: 13, padding: 8, lineHeight: 1.5,
                  background: '#fffbe6', borderColor: '#f0d68a' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
                    <input type="checkbox" checked={qDraft.approved} onChange={(e) => setQ('approved', e.target.checked)} />
                    <strong>Approved — ready to include in generated document</strong>
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary" onClick={saveAnswer} disabled={savingAnswer || !hasUnsavedChanges}>
                      {savingAnswer ? <><span className="spinner" />Saving...</> : hasUnsavedChanges ? 'Save Answer' : 'Saved'}
                    </button>
                  </div>
                </div>
                {hasUnsavedChanges &&
              <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#b7870a', marginTop: 8, marginBottom: 0 }}>
                    You have unsaved changes.
                  </p>
              }
              </div>

              {/* Nav between questions */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
                <button className="btn" disabled={questions.findIndex((q) => q.id === activeQid) === 0}
              onClick={() => {
                const idx = questions.findIndex((q) => q.id === activeQid);
                if (idx > 0) setActiveQid(questions[idx - 1].id);
              }}>← Previous Question</button>
                <button className="btn" disabled={questions.findIndex((q) => q.id === activeQid) === questions.length - 1}
              onClick={() => {
                const idx = questions.findIndex((q) => q.id === activeQid);
                if (idx < questions.length - 1) setActiveQid(questions[idx + 1].id);
              }}>Next Question →</button>
              </div>
            </>
          }
        </div>
      </div>

      {/* Generate panel (modal-ish — uses card layout to match firm style) */}
      {genOpen &&
      <div onClick={() => !generating && setGenOpen(false)}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
      }}>
          <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 6, maxWidth: 600, width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 20 }}>
            <h2 style={{ fontFamily: 'Times New Roman, serif', fontSize: 22, margin: '0 0 4px' }}>Generate Discovery Response</h2>
            <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#666', marginBottom: 16 }}>
              Select the attorney(s) who will sign the document. The signing attorney's name appears under the "By:" line on the Certificate of Service.
            </p>

            {stats.approved < stats.total &&
          <div className="alert" style={{
            background: '#fff8e1', color: '#5b4a08', border: '1px solid #f0d68a',
            padding: 10, borderRadius: 4, marginBottom: 14, fontFamily: 'Arial, sans-serif', fontSize: 12
          }}>
                <strong>{stats.total - stats.approved}</strong> answer{stats.total - stats.approved === 1 ? '' : 's'} not marked Approved. Check the box below to generate anyway.
              </div>
          }

            {/* Discovery Response Preamble — default is to include it. The default
                text lives in Admin → Discovery Settings. */}
            <div className="form-group">
              <label>Discovery Response Preamble</label>
              {discoveryDefaults.discovery_response_preamble && discoveryDefaults.discovery_response_preamble.trim() ?
              <>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: 13, marginBottom: 8 }}>
                    <input type="checkbox" checked={genState.includePreamble}
                  onChange={(e) => setGenState((p) => ({ ...p, includePreamble: e.target.checked }))} />
                    Include the default preamble at the top of the document
                  </label>
                  {genState.includePreamble &&
                <div style={{
                  padding: 10, border: '1px solid #e0e0e0', background: '#fafafa', borderRadius: 4,
                  fontFamily: 'Times New Roman, serif', fontSize: 13, lineHeight: 1.5,
                  maxHeight: 180, overflowY: 'auto', whiteSpace: 'pre-wrap', color: '#333'
                }}>
                      {discoveryDefaults.discovery_response_preamble}
                    </div>
                }
                  <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#888', marginTop: 6, marginBottom: 0 }}>
                    Change the default preamble in <strong>Admin → Discovery Settings</strong>.
                  </p>
                </> :

              <div style={{ fontSize: 12, color: '#888', fontFamily: 'Arial, sans-serif' }}>
                  No default preamble is configured. Set one in <strong>Admin → Discovery Settings</strong>.
                </div>
              }
            </div>

            <div className="form-group">
              <label>Signing attorneys</label>
              {eligibleAttorneys.length === 0 ?
            <div style={{ fontSize: 12, color: '#888', fontFamily: 'Arial, sans-serif' }}>
                  No eligible attorneys found. Attorneys need a role of "owner" or "attorney" and a bar number to sign filings.
                </div> :

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {eligibleAttorneys.map((a) =>
              <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'Arial, sans-serif', fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" checked={genState.attorneyIds.includes(a.id)} onChange={() => toggleAttorney(a.id)} />
                      <span><strong>{a.name}</strong> <span style={{ color: '#888' }}>· BPR {a.bar_number}</span></span>
                    </label>
              )}
                </div>
            }
            </div>

            {genState.attorneyIds.length > 1 &&
          <div className="form-group">
                <label>Signing attorney (the one whose name goes under "By:")</label>
                <select value={genState.signingAttorneyId} onChange={(e) => setGenState((p) => ({ ...p, signingAttorneyId: e.target.value }))}>
                  {genState.attorneyIds.map((aid) => {
                const a = admins.find((x) => x.id === aid);
                return <option key={aid} value={aid}>{a?.name || aid}</option>;
              })}
                </select>
              </div>
          }

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={genState.allowUnapproved}
              onChange={(e) => setGenState((p) => ({ ...p, allowUnapproved: e.target.checked }))} />
                Generate even if some answers are not approved
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, gap: 10 }}>
              <button className="btn" onClick={deleteResponse} disabled={generating}
            style={{ color: '#B85042' }}>Delete this response</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn" onClick={() => setGenOpen(false)} disabled={generating}>Cancel</button>
                <button className="btn btn-primary" onClick={handleGenerate}
              disabled={generating || genState.attorneyIds.length === 0}>
                  {generating ? <><span className="spinner" />Generating...</> : 'Generate Word Doc'}
                </button>
              </div>
            </div>
          </div>
        </div>
      }

      {/* Conflict modal (Phase 2) */}
      {conflict &&
      <div onClick={() => !savingAnswer && setConflict(null)}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
      }}>
          <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 6, maxWidth: 720, width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 20 }}>
            <h2 style={{ fontFamily: 'Times New Roman, serif', fontSize: 22, margin: '0 0 4px' }}>
              The client edited this answer
            </h2>
            <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#666', marginBottom: 16 }}>
              While you were editing, the client saved a new version of this answer. Choose which version
              to keep, or combine them.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: 4 }}>
                  Your version
                </div>
                <div style={{
                padding: 10, border: '1px solid #f0d68a', background: '#fff8e1', borderRadius: 4,
                fontFamily: 'Times New Roman, serif', fontSize: 13, lineHeight: 1.5,
                maxHeight: 200, overflowY: 'auto', whiteSpace: 'pre-wrap'
              }}>
                  {conflict.localDraft.answer_text || <em style={{ color: '#888' }}>(empty)</em>}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: 4 }}>
                  Client's version
                </div>
                <div style={{
                padding: 10, border: '1px solid #bcd4ee', background: '#e8f0fb', borderRadius: 4,
                fontFamily: 'Times New Roman, serif', fontSize: 13, lineHeight: 1.5,
                maxHeight: 200, overflowY: 'auto', whiteSpace: 'pre-wrap'
              }}>
                  {conflict.serverRow.answer_text || <em style={{ color: '#888' }}>(empty)</em>}
                </div>
              </div>
            </div>

            {conflict.mode === 'merge' &&
          <div className="form-group">
                <label>Edit the combined answer</label>
                <textarea value={conflict.mergeText}
            onChange={(e) => setConflict((p) => p ? { ...p, mergeText: e.target.value } : null)}
            rows={8}
            style={{ width: '100%', fontFamily: 'Times New Roman, serif', fontSize: 14, padding: 10, lineHeight: 1.5 }} />
                <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#888', marginTop: 6, marginBottom: 0 }}>
                  Your Approved checkbox and Attorney Notes are preserved as you set them — only the answer text is being merged.
                </p>
              </div>
          }

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              {conflict.mode === 'pick' ?
            <>
                  <button className="btn" onClick={() => setConflict(null)} disabled={savingAnswer}>Cancel</button>
                  <button className="btn" onClick={() => setConflict((p) => p ? { ...p, mode: 'merge', mergeText: p.serverRow.answer_text || '' } : null)} disabled={savingAnswer}>
                    Edit and save
                  </button>
                  <button className="btn" onClick={resolveKeepTheirs} disabled={savingAnswer}>
                    Keep client's version
                  </button>
                  <button className="btn btn-primary" onClick={resolveKeepMine} disabled={savingAnswer}>
                    {savingAnswer ? <><span className="spinner" />Saving...</> : 'Keep mine'}
                  </button>
                </> :

            <>
                  <button className="btn" onClick={() => setConflict((p) => p ? { ...p, mode: 'pick' } : null)} disabled={savingAnswer}>
                    ← Back
                  </button>
                  <button className="btn btn-primary" onClick={resolveMerge} disabled={savingAnswer}>
                    {savingAnswer ? <><span className="spinner" />Saving...</> : 'Save merged answer'}
                  </button>
                </>
            }
            </div>
          </div>
        </div>
      }
    </div>);

}

function StatCard({ label, value, color }) {
  return (
    <div className="card" style={{ margin: 0, textAlign: 'center', padding: 12 }}>
      <div style={{ fontSize: 22, fontFamily: 'Arial, sans-serif', color: color || '#0F1838', fontWeight: 600 }}>{value}</div>
      <div style={{ fontSize: 10, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    </div>);

}